/**
 * Pure helpers for the prospect-merge conflict picker.
 *
 * The picker only surfaces fields where the selected prospects actually DIFFER.
 * For each conflicting field the coach chooses among the candidate values,
 * defaulting to the survivor's value. When the survivor's value is empty but a
 * loser has one, we still surface the loser's value as a one-click alternative
 * so real data is never buried behind a null default.
 *
 * The resolved choices are folded into a `ResolvedFields` payload (PatchProspect
 * shape): scalars present are applied, scalars cleared go in `nullFields`, and
 * `about` / metrics dictionaries carry their resolved keys.
 */

import type { ProspectResponse, ResolvedFields } from "../../../../../types/api";
import { formatPlayerLevel } from "../../../../../constants/playerLevels";
import {
  formatEligibilityClass,
  formatJucoYear,
} from "../../../../../utils/roster-class";

/** Which section of the prospect a field belongs to. */
export type FieldSection = "scalar" | "about" | "positionPlayerMetrics" | "pitcherMetrics";

/** A single candidate value for a conflicting field, attributed to its source prospect. */
export type FieldCandidate = {
  /** The prospect this value came from. */
  prospectId: string;
  /** Raw value as stored on the prospect (string | number | null). */
  value: string | number | null;
};

/** A field where the selected prospects disagree and the coach must pick a value. */
export type FieldConflict = {
  section: FieldSection;
  /** camelCase key within its section (or top-level scalar key). */
  key: string;
  /** Human-friendly label for the picker. */
  label: string;
  /** Distinct candidate values across all selected prospects, survivor first. */
  candidates: FieldCandidate[];
  /**
   * The default selection — the survivor's value, unless the survivor's is empty
   * and someone else has a real one, in which case that wins. A blank default
   * would otherwise quietly throw away the only copy of an email or phone.
   */
  defaultProspectId: string;
};

const SCALAR_FIELDS: { key: string; label: string }[] = [
  { key: "firstName", label: "First Name" },
  { key: "lastName", label: "Last Name" },
  { key: "position", label: "Position" },
  { key: "gradYear", label: "Grad Year" },
  // The three roster-identity facts that sit beside Grad Year on the record. They were absent
  // from this list while the API was already merging them, which meant a merge could only ever
  // resolve them one way: survivor-wins, silently. That is exactly wrong for a level — a stored
  // `High School` written when the player was recruited out of high school would outlive every
  // merge with the JUCO record that superseded it, and the coach was never asked.
  { key: "playerLevel", label: "Level" },
  { key: "eligibilityClass", label: "Eligibility Class" },
  { key: "jucoYear", label: "JC Year" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "height", label: "Height" },
  { key: "weight", label: "Weight" },
  { key: "bats", label: "Bats" },
  { key: "throws", label: "Throws" },
];

/** Turn a camelCase metric/about key into a readable label ("popTime" → "Pop Time"). */
const humanizeKey = (key: string): string =>
  key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (c) => c.toUpperCase())
    .replace(/\b(\d)\b/g, "$1")
    .trim();

/** Normalize a raw section value to the comparable `string | number | null` union. */
const normalize = (value: unknown): string | number | null => {
  if (value === undefined || value === null) return null;
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length === 0 ? null : trimmed;
  }
  // Enums / booleans / dates — compare by their string form.
  return String(value);
};

/** Two values are equal for conflict purposes when their normalized forms match. */
const valuesEqual = (a: unknown, b: unknown): boolean => normalize(a) === normalize(b);

/** Read a field's raw value off a prospect for the given section/key. */
const readField = (
  prospect: ProspectResponse,
  section: FieldSection,
  key: string
): unknown => {
  if (section === "scalar") {
    const value = (prospect as unknown as Record<string, unknown>)[key];
    // `playerLevel` moved from About to the top level, and the API populates both for one
    // release. Read the authoritative column first and fall back, exactly as `types/api.ts`
    // instructs — otherwise a response from a server that predates the move reads as "no
    // level" on every record, no conflict is raised, and the merge picker stays silent about
    // the one field this whole list was extended for. `== null` rather than truthiness: 0 is
    // High School.
    if (key === "playerLevel" && value == null) {
      return prospect.about?.playerLevel ?? null;
    }
    return value;
  }
  const sectionObj = prospect[section] as Record<string, unknown> | null;
  return sectionObj ? sectionObj[key] : null;
};

/**
 * About keys the picker must not enumerate.
 *
 * `about.playerLevel` is the pre-move home of the top-level `playerLevel` and lands on the same
 * database column, so listing both offers the coach two rows for one fact — and lets them pick
 * contradictory answers, which the API then resolves by a back-compat precedence rule the picker
 * never showed them. The scalar row above is the one that survives the deprecation.
 */
const ABOUT_KEYS_HANDLED_AS_SCALARS = new Set<string>(["playerLevel"]);

/**
 * Collect the union of keys present across a section for the selected prospects.
 * Scalars use a fixed whitelist; about/metrics enumerate keys seen on any source.
 */
const collectSectionKeys = (
  prospects: ProspectResponse[],
  section: FieldSection
): { key: string; label: string }[] => {
  if (section === "scalar") return SCALAR_FIELDS;

  const keys = new Set<string>();
  for (const p of prospects) {
    const obj = p[section] as Record<string, unknown> | null;
    if (obj) {
      for (const k of Object.keys(obj)) {
        if (section === "about" && ABOUT_KEYS_HANDLED_AS_SCALARS.has(k)) continue;
        keys.add(k);
      }
    }
  }
  return [...keys].map((key) => ({ key, label: humanizeKey(key) }));
};

const SECTIONS: FieldSection[] = [
  "scalar",
  "about",
  "positionPlayerMetrics",
  "pitcherMetrics",
];

/**
 * Compute the set of conflicting fields across the selected prospects.
 *
 * A field conflicts when at least two prospects hold differing normalized values
 * (a present value vs. an empty one counts as a difference, so real data on a
 * loser surfaces even when the survivor's slot is blank). Only conflicting fields
 * are returned. Candidates are ordered survivor-first and de-duplicated by value.
 */
export const computeConflicts = (
  survivor: ProspectResponse,
  losers: ProspectResponse[]
): FieldConflict[] => {
  const all = [survivor, ...losers];
  const conflicts: FieldConflict[] = [];

  for (const section of SECTIONS) {
    for (const { key, label } of collectSectionKeys(all, section)) {
      const survivorValue = readField(survivor, section, key);

      // Does any participant differ from the survivor on this field?
      const differs = all.some((p) => !valuesEqual(readField(p, section, key), survivorValue));
      if (!differs) continue;

      // Build distinct candidates, survivor first, de-duped by normalized value.
      const seen = new Set<string | number | null>();
      const candidates: FieldCandidate[] = [];
      for (const p of all) {
        const norm = normalize(readField(p, section, key));
        if (seen.has(norm)) continue;
        seen.add(norm);
        candidates.push({ prospectId: p.id, value: norm });
      }

      // Survivor wins by default, but never with a blank: when the survivor has
      // nothing and another profile has a real value, that value is preselected.
      // The coach can still switch back to "—" to clear the field deliberately.
      const defaultProspectId =
        normalize(survivorValue) === null
          ? candidates.find((c) => c.value !== null)?.prospectId ?? survivor.id
          : survivor.id;

      conflicts.push({
        section,
        key,
        label,
        candidates,
        defaultProspectId,
      });
    }
  }

  return conflicts;
};

/**
 * A coach's resolution choices keyed by `"<section>.<key>"`. The value is the
 * prospect id whose value was chosen for that field.
 */
export type FieldChoices = Record<string, string>;

/** Stable composite key for a conflict ("scalar.email", "about.city", …). */
export const conflictId = (conflict: Pick<FieldConflict, "section" | "key">): string =>
  `${conflict.section}.${conflict.key}`;

/**
 * Seed the default choices for a list of conflicts — survivor wins every field.
 */
export const defaultChoices = (conflicts: FieldConflict[]): FieldChoices => {
  const choices: FieldChoices = {};
  for (const c of conflicts) {
    choices[conflictId(c)] = c.defaultProspectId;
  }
  return choices;
};

const SCALAR_KEYS = new Set(SCALAR_FIELDS.map((f) => f.key));

/**
 * Fold the coach's resolved choices into a `ResolvedFields` payload.
 *
 * Only fields the coach changed away from the survivor's current value are
 * emitted — survivor-wins fields need no write. A chosen empty scalar becomes a
 * `nullFields` clear; a chosen empty about/metric key is sent as `null` so the
 * PATCH apply path clears it.
 */
export const buildResolvedFields = (
  survivor: ProspectResponse,
  losers: ProspectResponse[],
  conflicts: FieldConflict[],
  choices: FieldChoices
): ResolvedFields => {
  const byId = new Map<string, ProspectResponse>(
    [survivor, ...losers].map((p) => [p.id, p])
  );
  const resolved: ResolvedFields = {};
  const nullFields: string[] = [];
  const about: Record<string, unknown> = {};
  const positionPlayerMetrics: Record<string, unknown> = {};
  const pitcherMetrics: Record<string, unknown> = {};

  for (const conflict of conflicts) {
    const chosenId = choices[conflictId(conflict)] ?? conflict.defaultProspectId;
    if (chosenId === survivor.id) continue; // survivor already holds this value

    const source = byId.get(chosenId);
    if (!source) continue;
    const value = normalize(readField(source, conflict.section, conflict.key));

    if (conflict.section === "scalar") {
      if (value === null) {
        if (SCALAR_KEYS.has(conflict.key)) nullFields.push(conflict.key);
      } else {
        (resolved as Record<string, unknown>)[conflict.key] = value;
      }
    } else if (conflict.section === "about") {
      about[conflict.key] = value;
    } else if (conflict.section === "positionPlayerMetrics") {
      positionPlayerMetrics[conflict.key] = value;
    } else if (conflict.section === "pitcherMetrics") {
      pitcherMetrics[conflict.key] = value;
    }
  }

  if (nullFields.length > 0) resolved.nullFields = nullFields;
  if (Object.keys(about).length > 0) resolved.about = about;
  if (Object.keys(positionPlayerMetrics).length > 0)
    resolved.positionPlayerMetrics = positionPlayerMetrics;
  if (Object.keys(pitcherMetrics).length > 0)
    resolved.pitcherMetrics = pitcherMetrics;

  return resolved;
};

/** Render a candidate value for display ("—" for empty). */
export const displayValue = (value: string | number | null): string => {
  if (value === null) return "—";
  return String(value);
};

/**
 * Scalar fields whose stored form is not the form a coach reads.
 *
 * A level is an ordinal and a JC year is a bare digit, so rendered raw the picker asks the coach
 * to choose between "0" and "1" — which is not a question anybody can answer. Each formatter is
 * the same one the rest of the app renders that field with, so the merge screen and the prospect
 * panel never disagree about what a value means.
 */
const SCALAR_VALUE_FORMATTERS: Record<
  string,
  (value: string | number | null) => string | null
> = {
  playerLevel: (value) => formatPlayerLevel(value as number | null),
  eligibilityClass: (value) => formatEligibilityClass(value as string | null),
  jucoYear: (value) => formatJucoYear(value as string | null),
};

/**
 * Render a candidate value the way its field reads to a coach, falling back to the raw form.
 *
 * An unrecognised stored value falls through to {@link displayValue} rather than rendering as
 * "—": a level ordinal outside the known set is bad data worth seeing, and hiding it behind an
 * empty dash would invite the coach to "resolve" it by picking the other record blind.
 */
export const displayFieldValue = (
  conflict: Pick<FieldConflict, "section" | "key">,
  value: string | number | null
): string => {
  if (conflict.section === "scalar" && value !== null) {
    const format = SCALAR_VALUE_FORMATTERS[conflict.key];
    if (format) return format(value) ?? displayValue(value);
  }
  return displayValue(value);
};

/**
 * A heuristic "completeness" score used to default the survivor pick to the
 * most-complete profile: count of non-empty scalar fields + note count.
 */
export const completenessScore = (prospect: ProspectResponse): number => {
  let score = prospect.noteCount ?? 0;
  for (const { key } of SCALAR_FIELDS) {
    if (normalize((prospect as unknown as Record<string, unknown>)[key]) !== null) {
      score += 1;
    }
  }
  if (prospect.about) score += 1;
  if (prospect.positionPlayerMetrics) score += 1;
  if (prospect.pitcherMetrics) score += 1;
  return score;
};

/** Pick the default survivor: the most-complete profile, ties broken by input order. */
export const pickDefaultSurvivor = (prospects: ProspectResponse[]): string => {
  let best = prospects[0];
  let bestScore = completenessScore(best);
  for (const p of prospects.slice(1)) {
    const score = completenessScore(p);
    if (score > bestScore) {
      best = p;
      bestScore = score;
    }
  }
  return best.id;
};

/** Total carry-over rows that will move from losers onto the survivor (for preview). */
export const carryOverCount = (prospect: ProspectResponse): number => {
  return prospect.noteCount ?? 0;
};

/** A readable display name for a prospect. */
export const prospectName = (prospect: ProspectResponse): string =>
  `${prospect.firstName} ${prospect.lastName}`.trim();
